import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from './api';

const AuthCtx = createContext(null);

/**
 * Auth context.
 *
 * Security model (Phase 5.1):
 *  - JWT lives ONLY in an HTTP-only cookie (`g66_token`) issued by the backend.
 *  - Frontend keeps a lightweight `user` object in memory (React state) for UI purposes.
 *  - On mount, we call `/auth/me` — if the cookie is valid, the session is restored.
 *  - No token is ever persisted in `localStorage` or `sessionStorage` (XSS mitigation).
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    // Backend responds with Set-Cookie: g66_token=...; HttpOnly; Secure; SameSite=Lax
    const r = await api.post('/auth/login', { email, password });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if the network call fails, clear local state so UI reflects logged out.
    }
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    const r = await api.get('/auth/me');
    setUser(r.data);
    return r.data;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, setUser }),
    [user, loading, login, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
